import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { silentError } from '../interceptors/silent-error-context';

export interface Chatbot {
  id: string;
  name: string;
  isActive: boolean;
  widgetConfig: Record<string, unknown>;
  aiConfig?: Record<string, unknown>;
  createdAt: string;
  // First time the embedded widget actually loaded on a real page, or null
  // if the snippet has never been pasted anywhere — see Chatbot.installedAt.
  installedAt?: string | null;
  _count?: { conversations: number; sources: number };
  // The paying account this chatbot belongs to — always the real owner's
  // id, whether the current viewer is that owner or an accepted teammate.
  // Use this (not the current user's own id) as the `ownerId` for Team API
  // calls, since an AGENT's own id is never the account to manage.
  userId: string;
  // Whether the current user owns this chatbot's account or has accepted
  // team-member access to it — see the `// Team` API block below. Absent
  // from responses that don't call findAllAccessible (e.g. getChatbot uses
  // the equivalent ownership check but doesn't currently tag the result).
  accountRole?: 'OWNER' | 'AGENT';
}

export type DemoStatus = 'PENDING' | 'GENERATING' | 'READY' | 'FAILED';

/** What an unauthenticated visitor to /d/:token receives. Deliberately narrow. */
export interface PublicProspectDemo {
  token: string;
  companyName: string | null;
  prospectUrl: string;
  chatbotId: string;
  /** Relative to the site origin (Caddy serves /demo-assets/* from a volume). */
  screenshotUrl: string | null;
  screenshotWidth: number | null;
  screenshotHeight: number | null;
  /** Phone-width capture. Null when that pass failed; the page falls back to desktop. */
  screenshotMobileUrl: string | null;
  screenshotMobileWidth: number | null;
  screenshotMobileHeight: number | null;
  /** False once the demo's message cap is spent — the page says so instead of failing mid-chat. */
  chatAvailable: boolean;
  expiresAt: string;
}

export interface ProspectImportEntry {
  url: string;
  companyName?: string;
  contactEmail?: string;
  /** Section prefixes to skip, from the review step. Prefixes, not URLs, so the
   *  choice still holds after the prospect publishes new pages. */
  excludedPaths?: string[];
}

/** One section of a site, as offered for review before anything is crawled. */
export interface SitemapCluster {
  prefix: string;
  label: string;
  pageCount: number;
  samples: string[];
  /** Whether it starts ticked — derived from the same scoring the crawl uses. */
  recommended: boolean;
}

export interface SitemapPreview {
  totalPages: number;
  crawlablePages: number;
  /** True when the site has more pages than the crawl budget allows. */
  exceedsBudget: boolean;
  clusters: SitemapCluster[];
}

export interface ProspectImportResult {
  created: number;
  skipped: { url: string; reason: string }[];
}

export interface DemoWidgetOverrides {
  primaryColor?: string;
  logoUrl?: string | null;
  headerTitle?: string;
  welcomeMessage?: string;
  fallbackMessage?: string;
  position?: 'bottom-right' | 'bottom-left';
  showBranding?: boolean;
}

/** One probe question and whether the demo bot could answer it. */
export interface DemoSmokeTestProbe {
  question: string;
  answered: boolean;
  answer: string;
}

export interface DemoSmokeTest {
  ranAt: string;
  passed: number;
  total: number;
  probes: DemoSmokeTestProbe[];
}

/** What a demo bot answered when the operator tested it from the admin panel. */
export interface DemoAnswer {
  answer: string;
  /** 'none' means the bot fell back — it had nothing to answer from. */
  confidence: string;
  citations: { url?: string; title?: string }[];
}

export interface ProspectDemoItem {
  id: string;
  token: string;
  prospectUrl: string;
  companyName: string | null;
  contactEmail: string | null;
  /** Scraped from the prospect's site during generation, best-first. Empty
   *  until the demo reaches READY, and empty for good on a site that publishes
   *  no address (a contact form only). */
  discoveredEmails: string[];
  /** ISO 639-1 from the prospect site's own <html lang>, or null when it
   *  declared nothing. Drives the demo widget's copy — and tells you which
   *  language to write the outreach email in. */
  language: string | null;
  /** Operator customisations. Null means the demo is still fully auto-derived
   *  from the prospect's site; absent keys within it mean the same per field. */
  widgetOverrides: DemoWidgetOverrides | null;
  /** Result of the probe questions this demo was asked right after generation.
   *  Null means the check never ran — a demo built before it existed, or a run
   *  that itself failed — which is NOT the same as `passed: 0`, a demo that
   *  answered nothing and is the one most worth looking at before sending. */
  smokeTest: DemoSmokeTest | null;
  status: DemoStatus;
  error: string | null;
  pageCount: number;
  expiresAt: string;
  messageCount: number;
  messageCap: number;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  openCount: number;
  firstMessageAt: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  hasScreenshot: boolean;
  isExpired: boolean;
}

export interface ProspectDemoList {
  items: ProspectDemoItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ChatbotStats {
  todayConversations: number;
  unansweredCount: number;
  isActive: boolean;
}

export interface NotificationPrefs {
  emailOnNewConversation: boolean;
  emailOnUnanswered: boolean;
  dailySummary: boolean;
  emailOnHandoffRequest: boolean;
  emailOnQuotaWarning: boolean;
  emailOnCrawlComplete: boolean;
  emailOnUpgradeNudge: boolean;
}

export interface ChatbotUsage {
  month: string;
  messageCount: number;
  tokenCount: number;
  messageCredits: number;
}

/** Unanswered questions grouped by the topic they're actually about. */
export interface CoverageGap {
  /** The group's shortest phrasing — it reads as the topic, not as one person's version. */
  question: string;
  /** How many separate visitors asked something in this group. */
  count: number;
  examples: string[];
  lastAskedAt: string;
}

export interface ChatbotAnalytics {
  days: number;
  conversationsByDay: { date: string; count: number }[];
  confidenceBreakdown: { high: number; medium: number; low: number; none: number };
  unansweredQuestions: { conversationId: string; question: string; answeredAt: string }[];
  /** The same unanswered questions, grouped and ranked by how many people asked. */
  coverageGaps: CoverageGap[];
  lowRatedAnswers: { conversationId: string; question: string; answer: string; answeredAt: string }[];
}

export interface ContentSource {
  id: string;
  type: 'URL' | 'FILE' | 'TEXT' | 'QA';
  url?: string;
  fileName?: string;
  status: 'PENDING' | 'CRAWLING' | 'COMPLETED' | 'FAILED';
  pageCount: number;
  error?: string | null;
  createdAt: string;
  _count?: { chunks: number };
}

/** One page a crawl covered, reconstructed from its chunks' metadata. */
export interface CrawledPage {
  url: string | null;
  title: string | null;
  chunkCount: number;
  /** Total characters extracted — a low value on a real page usually means a
   *  JS-rendered site whose raw server HTML was nearly empty. */
  textLength: number;
}

/** One section of what a crawl actually indexed. */
export interface CrawledSection {
  prefix: string;
  label: string;
  pageCount: number;
  chunkCount: number;
  samples: string[];
  /** False for the root section — removing it would empty the source. */
  removable: boolean;
}

export interface PageChunk {
  id: string;
  content: string;
}

export interface RetrievalPreview {
  /** Similarity a chunk must reach to be used in an answer. */
  threshold: number;
  /** How many chunks the answering path considers at all. */
  topK: number;
  results: {
    content: string;
    url: string | null;
    title: string | null;
    similarity: number;
    /** Whether the answering path would actually have used this chunk. */
    used: boolean;
  }[];
}

export interface QaPair {
  question: string;
  answer: string;
}

export interface Conversation {
  id: string;
  visitorId: string;
  status: 'AI_HANDLING' | 'HUMAN_TAKEOVER' | 'RESOLVED';
  createdAt: string;
  updatedAt: string;
  messages?: Message[];
  _count?: { messages: number };
}

export interface Message {
  id: string;
  content: string;
  senderType: 'VISITOR' | 'AI' | 'HUMAN';
  sourceCitations?: unknown;
  createdAt: string;
}

export interface Lead {
  id: string;
  visitorId: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  createdAt: string;
}

export interface LeadsPage {
  leads: Lead[];
  /** True total for this chatbot — may exceed leads.length, which is capped server-side. */
  total: number;
}

export type TicketCategory = 'BUG' | 'FEATURE_REQUEST' | 'QUESTION' | 'OTHER';
export type TicketStatus = 'OPEN' | 'RESOLVED';

export interface SupportTicket {
  id: string;
  category: TicketCategory;
  message: string;
  status: TicketStatus;
  adminNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TeamMember {
  id: string;
  role: 'OWNER' | 'AGENT';
  createdAt: string;
  userId: string;
  email: string;
  name: string | null;
}

export interface PendingInvite {
  id: string;
  email: string;
  role: 'OWNER' | 'AGENT';
  createdAt: string;
  expiresAt: string;
}

export interface TeamAccount {
  owner: { id: string; email: string; name: string | null };
  // null means unlimited (AGENCY plan) — Infinity doesn't survive JSON.
  seatLimit: number | null;
  seatsUsed: number;
  members: TeamMember[];
  pendingInvites: PendingInvite[];
}

export interface InvitePreview {
  email: string;
  ownerName: string | null;
  role: 'OWNER' | 'AGENT';
  alreadyHasAccount: boolean;
}

export interface AcceptInviteResult {
  accessToken: string;
  user: { id: string; email: string; name: string | null; plan: string; role: string; emailVerified: boolean };
}

// --- Superadmin ---

export interface AdminOverview {
  users: {
    total: number;
    newLast30d: number;
    byPlan: Record<string, number>;
    suspended: number;
  };
  chatbots: { total: number; active: number };
  sources: {
    byType: Record<string, { count: number; pages: number }>;
    byStatus: Record<string, number>;
    totalPages: number;
  };
  chunks: { total: number };
  usageThisMonth: { messages: number; tokens: number; estCostUsd: number };
  conversationsThisMonth: number;
  estMrr: number;
}

export interface AdminTrends {
  days: number;
  signupsByDay: { date: string; count: number }[];
  messagesByDay: { date: string; count: number }[];
}

export interface AdminTopUsage {
  chatbotId: string;
  chatbotName: string;
  ownerId: string;
  ownerEmail: string;
  ownerPlan: string;
  messageCount: number;
  tokenCount: number;
  estCostUsd: number;
  messageCredits: number;
}

export interface AdminStorageUser {
  userId: string;
  email: string;
  plan: string;
  chatbots: number;
  sources: number;
  pages: number;
  chunks: number;
  contentBytes: number;
  embeddingBytes: number;
  conversations: number;
  messages: number;
  messageBytes: number;
  leads: number;
  estimatedBytes: number;
}

export interface AdminStorage {
  dbSizeBytes: number;
  users: AdminStorageUser[];
}

export interface AdminSystem {
  timestamp: string;
  process: {
    nodeVersion: string;
    uptimeSec: number;
    memory: { rssBytes: number; heapUsedBytes: number; heapTotalBytes: number };
    eventLoop: { meanMs: number; maxMs: number };
  };
  database: { up: boolean; sizeBytes: number; activeConnections: number; latencyMs: number };
  redis: { up: boolean; usedMemoryBytes: number; maxMemoryBytes: number };
  queue: {
    counts: { waiting: number; active: number; completed: number; failed: number; delayed: number };
    recentFailures: { id: string; name: string; sourceId: string | null; reason: string; failedAt: string | null }[];
  };
  disk: { totalBytes: number; freeBytes: number };
  websocket: {
    total: number;
    visitors: number;
    dashboards: number;
    byChatbot: { chatbotId: string; chatbotName: string; ownerEmail: string | null; visitors: number; dashboards: number }[];
  };
}

export interface AdminUserListItem {
  id: string;
  email: string;
  name: string | null;
  plan: string;
  planOverride: boolean;
  role: string;
  isSuspended: boolean;
  emailVerified: boolean;
  createdAt: string;
  chatbotCount: number;
  monthMessages: number;
}

export interface AdminUserList {
  items: AdminUserListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminUserDetail {
  id: string;
  email: string;
  name: string | null;
  plan: string;
  planOverride: boolean;
  role: string;
  isSuspended: boolean;
  emailVerified: boolean;
  hasStripeCustomer: boolean;
  createdAt: string;
  chatbots: {
    id: string;
    name: string;
    isActive: boolean;
    sourceCount: number;
    conversationCount: number;
    monthMessages: number;
    monthTokens: number;
  }[];
  activity: { lastBotUpdateAt: string | null; lastConversationAt: string | null };
  recentAuditEntries: { action: string; detail: unknown; createdAt: string; actorEmail: string }[];
}

export interface AdminSourceItem {
  id: string;
  type: 'URL' | 'FILE' | 'TEXT' | 'QA';
  url: string | null;
  fileName: string | null;
  status: 'PENDING' | 'CRAWLING' | 'COMPLETED' | 'FAILED';
  error: string | null;
  pageCount: number;
  createdAt: string;
  chatbotId: string;
  chatbotName: string;
  ownerId: string;
  ownerEmail: string;
}

export interface AdminSourceList {
  items: AdminSourceItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminAuditEntry {
  id: string;
  action: string;
  actorEmail: string;
  targetUserId: string | null;
  targetUserEmail: string | null;
  detail: unknown;
  createdAt: string;
}

export interface AdminAuditList {
  items: AdminAuditEntry[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminTicket {
  id: string;
  category: TicketCategory;
  message: string;
  status: TicketStatus;
  adminNote: string | null;
  createdAt: string;
  updatedAt: string;
  user: { id: string; email: string; name: string | null };
}

export interface AdminTicketList {
  items: AdminTicket[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ChurnFeedbackEntry {
  id: string;
  userEmail: string;
  plan: string;
  feedback: string | null;
  comment: string | null;
  createdAt: string;
}

export interface ChurnFeedbackList {
  items: ChurnFeedbackEntry[];
  total: number;
  page: number;
  pageSize: number;
  byFeedback: { feedback: string; count: number }[];
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(private readonly http: HttpClient) {}

  // Chatbot CRUD
  getChatbots(): Observable<Chatbot[]> {
    return this.http.get<Chatbot[]>('/api/chatbots');
  }

  getChatbot(id: string): Observable<Chatbot> {
    return this.http.get<Chatbot>(`/api/chatbots/${id}`);
  }

  getChatbotStats(id: string): Observable<ChatbotStats> {
    return this.http.get<ChatbotStats>(`/api/chatbots/${id}/stats`);
  }

  getChatbotAnalytics(id: string, days = 7): Observable<ChatbotAnalytics> {
    return this.http.get<ChatbotAnalytics>(`/api/chatbots/${id}/analytics?days=${days}`);
  }

  getChatbotUsage(id: string): Observable<ChatbotUsage> {
    return this.http.get<ChatbotUsage>(`/api/chatbots/${id}/usage`);
  }

  createChatbot(name: string): Observable<Chatbot> {
    return this.http.post<Chatbot>('/api/chatbots', { name });
  }

  updateChatbot(id: string, data: Partial<Chatbot>): Observable<Chatbot> {
    return this.http.put<Chatbot>(`/api/chatbots/${id}`, data);
  }

  deleteChatbot(id: string): Observable<void> {
    return this.http.delete<void>(`/api/chatbots/${id}`);
  }

  // Content Sources
  // `silent` is for periodic polling (crawl-status checks) — one transient
  // failure mid-poll shouldn't interrupt the user with a toast every couple
  // of seconds; the poller tracks consecutive failures itself instead.
  getSources(chatbotId: string, opts?: { silent?: boolean }): Observable<ContentSource[]> {
    return this.http.get<ContentSource[]>(
      `/api/chatbots/${chatbotId}/sources`,
      opts?.silent ? { context: silentError() } : {},
    );
  }

  previewContentSitemap(chatbotId: string, url: string): Observable<SitemapPreview> {
    return this.http.post<SitemapPreview>(
      `/api/chatbots/${chatbotId}/sources/preview-sitemap`,
      { url },
    );
  }

  addUrlSource(chatbotId: string, url: string, excludedPaths: string[] = []): Observable<ContentSource> {
    return this.http.post<ContentSource>(`/api/chatbots/${chatbotId}/sources/url`, { url, excludedPaths });
  }

  /** Ready-to-send test questions built from completed sources' titles — used
   *  by the onboarding wizard's test step. */
  getSuggestedQuestions(chatbotId: string): Observable<string[]> {
    return this.http.get<string[]>(`/api/chatbots/${chatbotId}/sources/suggested-questions`, { context: silentError() });
  }

  addFileSource(chatbotId: string, file: File): Observable<ContentSource> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ContentSource>(`/api/chatbots/${chatbotId}/sources/file`, formData);
  }

  /**
   * Same endpoint as addFileSource, but observed as a raw event stream so
   * the caller can render upload progress — large files otherwise give zero
   * feedback between file-select and the list refreshing minutes later.
   */
  addFileSourceWithProgress(chatbotId: string, file: File): Observable<HttpEvent<ContentSource>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ContentSource>(`/api/chatbots/${chatbotId}/sources/file`, formData, {
      reportProgress: true,
      observe: 'events',
    });
  }

  addTextSource(chatbotId: string, content: string, title?: string): Observable<ContentSource> {
    return this.http.post<ContentSource>(`/api/chatbots/${chatbotId}/sources/text`, { content, title });
  }

  addQaSource(chatbotId: string, pairs: QaPair[], title?: string): Observable<ContentSource> {
    return this.http.post<ContentSource>(`/api/chatbots/${chatbotId}/sources/qa`, { pairs, title });
  }

  /** The pages a URL source actually crawled — there is no per-page table, so
   *  the API groups these out of chunk metadata. */
  getCrawledPages(
    chatbotId: string,
    sourceId: string,
  ): Observable<{ pages: CrawledPage[]; sections: CrawledSection[] }> {
    return this.http.get<{ pages: CrawledPage[]; sections: CrawledSection[] }>(
      `/api/chatbots/${chatbotId}/sources/${sourceId}/pages`,
    );
  }

  /** Removes a section's pages AND stops later recrawls re-adding them. */
  removeCrawledSection(
    chatbotId: string,
    sourceId: string,
    prefix: string,
  ): Observable<{ removedChunks: number; remainingPages: number; excludedPath: string }> {
    return this.http.request<{ removedChunks: number; remainingPages: number; excludedPath: string }>(
      'DELETE',
      `/api/chatbots/${chatbotId}/sources/${sourceId}/sections`,
      { body: { prefix } },
    );
  }

  /** The extracted text for one crawled page. This is untrusted crawled
   *  content — render it escaped, never as HTML. */
  getPageChunks(
    chatbotId: string,
    sourceId: string,
    url: string,
  ): Observable<{ url: string; chunks: PageChunk[] }> {
    return this.http.get<{ url: string; chunks: PageChunk[] }>(
      `/api/chatbots/${chatbotId}/sources/${sourceId}/chunks`,
      { params: { url } },
    );
  }

  /** What retrieval would return for a question, including the near misses
   *  that scored below the relevance floor. Costs one embedding, no
   *  completion, and nothing against the account's message credits. */
  previewRetrieval(chatbotId: string, question: string): Observable<RetrievalPreview> {
    return this.http.post<RetrievalPreview>(`/api/chatbots/${chatbotId}/retrieval/preview`, {
      question,
    });
  }

  getQaPairs(chatbotId: string, sourceId: string): Observable<{ title: string; pairs: QaPair[] }> {
    return this.http.get<{ title: string; pairs: QaPair[] }>(`/api/chatbots/${chatbotId}/sources/${sourceId}/qa`);
  }

  updateQaSource(chatbotId: string, sourceId: string, pairs: QaPair[], title?: string): Observable<unknown> {
    return this.http.post(`/api/chatbots/${chatbotId}/sources/${sourceId}/qa`, { pairs, title });
  }

  deleteSource(chatbotId: string, sourceId: string): Observable<void> {
    return this.http.delete<void>(`/api/chatbots/${chatbotId}/sources/${sourceId}`);
  }

  recrawlSource(chatbotId: string, sourceId: string): Observable<unknown> {
    return this.http.post(`/api/chatbots/${chatbotId}/sources/${sourceId}/recrawl`, {});
  }

  // Conversations
  getConversations(chatbotId: string): Observable<Conversation[]> {
    return this.http.get<Conversation[]>(`/api/chatbots/${chatbotId}/conversations`);
  }

  getMessages(conversationId: string): Observable<Message[]> {
    return this.http.get<Message[]>(`/api/conversations/${conversationId}/messages`);
  }

  takeOver(conversationId: string): Observable<unknown> {
    return this.http.post(`/api/conversations/${conversationId}/take-over`, {});
  }

  handBack(conversationId: string): Observable<unknown> {
    return this.http.post(`/api/conversations/${conversationId}/hand-back`, {});
  }

  resolveConversation(conversationId: string): Observable<unknown> {
    return this.http.post(`/api/conversations/${conversationId}/resolve`, {});
  }

  sendHumanReply(conversationId: string, content: string): Observable<Message> {
    return this.http.post<Message>(`/api/conversations/${conversationId}/reply`, { content });
  }

  // Widget config
  updateWidgetConfig(chatbotId: string, config: Record<string, unknown>): Observable<unknown> {
    return this.http.put(`/api/chatbots/${chatbotId}/widget-config`, config);
  }

  // AI behavior config
  updateAiConfig(chatbotId: string, config: Record<string, unknown>): Observable<unknown> {
    return this.http.put(`/api/chatbots/${chatbotId}/ai-config`, config);
  }

  // Leads
  getLeads(chatbotId: string): Observable<LeadsPage> {
    return this.http.get<LeadsPage>(`/api/chatbots/${chatbotId}/leads`);
  }

  // Public widget endpoint (DomainAllowlistGuard, not JwtAuthGuard) — also
  // used from the dashboard's own "test your chatbot" preview, which is why
  // this lives here rather than only being called via raw fetch() like the
  // embeddable widget does.
  submitLead(
    chatbotId: string,
    payload: { visitorId: string; name?: string; email?: string },
  ): Observable<unknown> {
    return this.http.post(`/api/chatbots/${chatbotId}/leads`, payload);
  }

  // Support tickets (product feedback / bug reports — distinct from
  // per-message visitor feedback on the analytics page)
  createSupportTicket(category: TicketCategory, message: string): Observable<SupportTicket> {
    return this.http.post<SupportTicket>('/api/support/tickets', { category, message });
  }

  getMySupportTickets(): Observable<SupportTicket[]> {
    return this.http.get<SupportTicket[]>('/api/support/tickets');
  }

  // Account / profile
  updateProfile(name: string): Observable<{ id: string; email: string; name: string | null; plan: string }> {
    return this.http.put<{ id: string; email: string; name: string | null; plan: string }>('/api/auth/me', { name });
  }

  deleteAccount(): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>('/api/auth/me');
  }

  exportData(): Observable<unknown> {
    return this.http.get<unknown>('/api/auth/me/export');
  }

  getNotificationPrefs(): Observable<NotificationPrefs> {
    return this.http.get<NotificationPrefs>('/api/auth/me/notifications');
  }

  updateNotificationPrefs(prefs: Partial<NotificationPrefs>): Observable<NotificationPrefs> {
    return this.http.put<NotificationPrefs>('/api/auth/me/notifications', prefs);
  }

  // Billing
  getBillingStatus(): Observable<{ configured: boolean }> {
    return this.http.get<{ configured: boolean }>('/api/billing/status');
  }

  // Deployment-shaped config the frontend can't infer from window.location
  // alone (widget bundle's CDN origin — see embed-snippet.ts — plus the
  // optional GA measurement ID read by GoogleAnalyticsService).
  getPlatformConfig(): Observable<{
    widgetUrl: string | null;
    analytics: { googleAnalyticsId: string } | null;
    turnstileSiteKey: string | null;
  }> {
    return this.http.get<{
      widgetUrl: string | null;
      analytics: { googleAnalyticsId: string } | null;
      turnstileSiteKey: string | null;
    }>('/api/platform-config');
  }

  createCheckoutSession(plan: string): Observable<{ url: string }> {
    return this.http.post<{ url: string }>('/api/billing/checkout', { plan });
  }

  createPortalSession(): Observable<{ url: string }> {
    return this.http.post<{ url: string }>('/api/billing/portal', {});
  }

  // Team
  getTeamMembers(ownerId: string): Observable<TeamAccount> {
    return this.http.get<TeamAccount>(`/api/account/members?ownerId=${ownerId}`);
  }

  inviteTeamMember(ownerId: string, data: { email: string; role?: 'OWNER' | 'AGENT' }): Observable<PendingInvite> {
    return this.http.post<PendingInvite>('/api/account/invites', { ownerId, ...data });
  }

  revokeInvite(inviteId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`/api/account/invites/${inviteId}`);
  }

  setTeamMemberRole(membershipId: string, role: 'OWNER' | 'AGENT'): Observable<{ id: string; role: string }> {
    return this.http.patch<{ id: string; role: string }>(`/api/account/members/${membershipId}/role`, { role });
  }

  removeTeamMember(membershipId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`/api/account/members/${membershipId}`);
  }

  getInvitePreview(token: string): Observable<InvitePreview> {
    return this.http.get<InvitePreview>(`/api/account/invites/${token}`);
  }

  acceptInviteAsNewUser(token: string, data: { name?: string; password: string }): Observable<AcceptInviteResult> {
    return this.http.post<AcceptInviteResult>(`/api/account/invites/${token}/accept`, data);
  }

  acceptInviteAsExistingUser(token: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`/api/account/invites/${token}/accept-existing`, {});
  }

  // Superadmin
  getAdminOverview(): Observable<AdminOverview> {
    return this.http.get<AdminOverview>('/api/admin/stats/overview');
  }

  getAdminTrends(days = 30): Observable<AdminTrends> {
    return this.http.get<AdminTrends>(`/api/admin/stats/trends?days=${days}`);
  }

  getAdminTopUsage(): Observable<AdminTopUsage[]> {
    return this.http.get<AdminTopUsage[]>('/api/admin/stats/top-usage');
  }

  getAdminStorage(): Observable<AdminStorage> {
    return this.http.get<AdminStorage>('/api/admin/stats/storage');
  }

  getAdminSystem(): Observable<AdminSystem> {
    return this.http.get<AdminSystem>('/api/admin/system');
  }

  getAdminUsers(query: { search?: string; plan?: string; page?: number } = {}): Observable<AdminUserList> {
    const params = new URLSearchParams();
    if (query.search) params.set('search', query.search);
    if (query.plan) params.set('plan', query.plan);
    if (query.page) params.set('page', String(query.page));
    const qs = params.toString();
    return this.http.get<AdminUserList>(`/api/admin/users${qs ? `?${qs}` : ''}`);
  }

  getAdminUser(id: string): Observable<AdminUserDetail> {
    return this.http.get<AdminUserDetail>(`/api/admin/users/${id}`);
  }

  createAdminUser(data: { email: string; name?: string; plan?: string }): Observable<AdminUserListItem> {
    return this.http.post<AdminUserListItem>('/api/admin/users', data);
  }

  setAdminUserPlan(id: string, plan: string): Observable<{ id: string; plan: string; planOverride: boolean }> {
    return this.http.patch<{ id: string; plan: string; planOverride: boolean }>(`/api/admin/users/${id}/plan`, { plan });
  }

  releaseAdminPlanOverride(id: string): Observable<{ id: string; plan: string; planOverride: boolean }> {
    return this.http.delete<{ id: string; plan: string; planOverride: boolean }>(`/api/admin/users/${id}/plan-override`);
  }

  setAdminUserSuspension(id: string, suspended: boolean): Observable<{ id: string; isSuspended: boolean }> {
    return this.http.patch<{ id: string; isSuspended: boolean }>(`/api/admin/users/${id}/suspension`, { suspended });
  }

  getAdminSources(query: { status?: string; page?: number } = {}): Observable<AdminSourceList> {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    if (query.page) params.set('page', String(query.page));
    const qs = params.toString();
    return this.http.get<AdminSourceList>(`/api/admin/sources${qs ? `?${qs}` : ''}`);
  }

  getAdminAuditLog(page = 1): Observable<AdminAuditList> {
    return this.http.get<AdminAuditList>(`/api/admin/audit-log?page=${page}`);
  }

  getAdminTickets(query: { status?: TicketStatus; page?: number } = {}): Observable<AdminTicketList> {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    if (query.page) params.set('page', String(query.page));
    const qs = params.toString();
    return this.http.get<AdminTicketList>(`/api/admin/tickets${qs ? `?${qs}` : ''}`);
  }

  resolveAdminTicket(id: string, adminNote?: string): Observable<AdminTicket> {
    return this.http.patch<AdminTicket>(`/api/admin/tickets/${id}/resolve`, { adminNote });
  }

  getAdminChurnFeedback(page = 1): Observable<ChurnFeedbackList> {
    return this.http.get<ChurnFeedbackList>(`/api/admin/churn?page=${page}`);
  }

  // Demo (no auth required)
  demoCrawl(url: string, captchaToken?: string): Observable<{ chatbotId: string; pages: number }> {
    return this.http.post<{ chatbotId: string; pages: number }>('/api/demo/crawl', { url, captchaToken });
  }

  demoChat(chatbotId: string, message: string): Observable<{ answer: string; citations: unknown[] }> {
    return this.http.post<{ answer: string; citations: unknown[] }>('/api/demo/chat', { chatbotId, message });
  }

  // Outreach: prospect demos (superadmin tooling + one public read)

  /** Public — the payload /d/:token renders itself from. No auth, by design. */
  getProspectDemo(token: string): Observable<PublicProspectDemo> {
    return this.http.get<PublicProspectDemo>(`/api/demos/${encodeURIComponent(token)}`, {
      // The page renders its own not-found / expired states from the status
      // code, so the global error toast would be duplicate noise.
      context: silentError(),
    });
  }

  askProspectDemo(demoId: string, question: string): Observable<DemoAnswer> {
    return this.http.post<DemoAnswer>(`/api/admin/outreach/demos/${demoId}/ask`, { question });
  }

  /** What a demo's crawl actually indexed, grouped into sections — same shape
   *  as getCrawledPages, for the dashboard content page's equivalent panel. */
  getDemoPages(demoId: string): Observable<{ pages: CrawledPage[]; sections: CrawledSection[] }> {
    return this.http.get<{ pages: CrawledPage[]; sections: CrawledSection[] }>(
      `/api/admin/outreach/demos/${demoId}/pages`,
    );
  }

  /** Removes a section from a demo's index AND stops a later regenerate re-adding it. */
  removeDemoSection(
    demoId: string,
    prefix: string,
  ): Observable<{ removedChunks: number; remainingPages: number; excludedPath: string }> {
    return this.http.request<{ removedChunks: number; remainingPages: number; excludedPath: string }>(
      'DELETE',
      `/api/admin/outreach/demos/${demoId}/sections`,
      { body: { prefix } },
    );
  }

  previewProspectSitemap(url: string): Observable<SitemapPreview> {
    return this.http.post<SitemapPreview>('/api/admin/outreach/demos/preview-sitemap', { url });
  }

  importProspects(prospects: ProspectImportEntry[]): Observable<ProspectImportResult> {
    return this.http.post<ProspectImportResult>('/api/admin/outreach/demos', { prospects });
  }

  getProspectDemos(query: { status?: DemoStatus; page?: number } = {}): Observable<ProspectDemoList> {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    if (query.page) params.set('page', String(query.page));
    const qs = params.toString();
    return this.http.get<ProspectDemoList>(`/api/admin/outreach/demos${qs ? `?${qs}` : ''}`);
  }

  /** CSV text, not a download URL — the route is bearer-authenticated, so the
   *  response has to come through HttpClient's interceptors and be turned into
   *  a blob client-side rather than opened as a plain link. */
  exportProspectDemosCsv(status?: DemoStatus): Observable<string> {
    const qs = status ? `?status=${status}` : '';
    return this.http.get(`/api/admin/outreach/demos/export${qs}`, { responseType: 'text' });
  }

  updateProspectDemoWidget(
    id: string,
    patch: DemoWidgetOverrides,
  ): Observable<{ widgetOverrides: DemoWidgetOverrides }> {
    return this.http.patch<{ widgetOverrides: DemoWidgetOverrides }>(
      `/api/admin/outreach/demos/${id}/widget`,
      patch,
    );
  }

  resetProspectDemoWidget(id: string): Observable<{ widgetOverrides: null }> {
    return this.http.delete<{ widgetOverrides: null }>(`/api/admin/outreach/demos/${id}/widget`);
  }

  regenerateProspectDemo(id: string): Observable<{ queued: boolean }> {
    return this.http.post<{ queued: boolean }>(`/api/admin/outreach/demos/${id}/regenerate`, {});
  }

  extendProspectDemo(id: string, days: number): Observable<{ expiresAt: string }> {
    return this.http.patch<{ expiresAt: string }>(`/api/admin/outreach/demos/${id}/extend`, { days });
  }

  raiseProspectDemoCap(id: string, additional: number): Observable<{ messageCap: number }> {
    return this.http.patch<{ messageCap: number }>(`/api/admin/outreach/demos/${id}/message-cap`, { additional });
  }

  deleteProspectDemo(id: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`/api/admin/outreach/demos/${id}`);
  }
}
